const runtimeConfig =
  typeof window !== "undefined" && window.__WLJ_CONFIG__
    ? window.__WLJ_CONFIG__
    : {};

const env = process.env || {};

const pickConfig = (key, fallback = "") => {
  const runtimeValue = runtimeConfig[key];

  if (
    runtimeValue !== undefined &&
    runtimeValue !== null &&
    runtimeValue !== ""
  ) {
    return runtimeValue;
  }

  const envValue = env[`REACT_APP_${key}`];

  if (envValue !== undefined && envValue !== null && envValue !== "") {
    return envValue;
  }

  return fallback;
};

const normalizeBoolean = (value, fallback = false) => {
  if (value === undefined || value === null || value === "") return fallback;

  if (typeof value === "boolean") return value;

  return ["1", "true", "yes", "on"].includes(String(value).toLowerCase());
};

const trimSlashes = (value) => String(value || "").replace(/^\/+|\/+$/g, "");

const splitConfiguredUrl = (value) => {
  const normalizedValue = String(value || "").trim();

  if (!normalizedValue) {
    return { origin: "", path: "" };
  }

  try {
    const parsedUrl = new URL(normalizedValue);

    return {
      origin: `${parsedUrl.protocol}//${parsedUrl.host}`,

      path: trimSlashes(parsedUrl.pathname),
    };
  } catch {
    return {
      origin: "",

      path: trimSlashes(normalizedValue),
    };
  }
};

export const joinUrl = (base, path = "") => {
  const cleanBase = String(base || "").replace(/\/+$/g, "");

  const cleanPath = trimSlashes(path);

  if (!cleanBase) return cleanPath ? `/${cleanPath}` : "";

  return cleanPath ? `${cleanBase}/${cleanPath}` : cleanBase;
};

// ---------------------------------------------------------------------------

// Single switches — change these (or REACT_APP_* / window.__WLJ_CONFIG__) once.

// Everything else in the app should import from here (axios already does).

// ---------------------------------------------------------------------------

/** Hostnames treated as production (comma-separated). preprod.* is always non-production. */

export const PRODUCTION_HOSTS = pickConfig(

  "PRODUCTION_HOSTS",

  "welinkjobs.com",

)

  .split(",")

  .map((host) => host.trim().toLowerCase())

  .filter(Boolean);



const resolveIsProductionFromDomain = () => {

  if (typeof window === "undefined") return null;



  const host = String(window.location.hostname || "").toLowerCase();

  if (host.startsWith("preprod.")) return false;



  const isProdHost = PRODUCTION_HOSTS.some(

    (prodHost) => host === prodHost || host.endsWith(`.${prodHost}`),

  );

  if (isProdHost) return true;



  // localhost / other dev hosts — let env override decide

  return null;

};



const resolveIsProduction = () => {

  const envValue = process.env.REACT_APP_IS_PRODUCTION;

  if (envValue !== undefined && envValue !== null && envValue !== "") {

    return normalizeBoolean(envValue, false);

  }



  const fromDomain = resolveIsProductionFromDomain();

  if (fromDomain !== null) {

    return fromDomain;

  }



  return normalizeBoolean(pickConfig("IS_PRODUCTION", "false"), false);

};



/** true on main domain; false on preprod / localhost. Override via REACT_APP_IS_PRODUCTION. */

export const IS_PRODUCTION = resolveIsProduction();



/** CDN root — change once; CDN_PUBLIC_PATH / CDN_ASSETS_BASE_URL are derived. */

const DEFAULT_CDN_ORIGIN = "https://wlj-cdn.sfo2.digitaloceanspaces.com";

/** API host when API_ORIGIN / API_BASE_URL are not overridden. */

const DEFAULT_API_ORIGIN = IS_PRODUCTION
  ? "https://account.welinkjobs.com"
  : "https://preprod.welinkjobs.com";

const configuredApiBaseUrl = pickConfig(
  "API_BASE_URL",

  pickConfig("PUBLIC_API_BASE_URL", ""),
);

const parsedApiBaseUrl = splitConfiguredUrl(configuredApiBaseUrl);

export const API_ORIGIN = pickConfig(
  "API_ORIGIN",

  parsedApiBaseUrl.origin || DEFAULT_API_ORIGIN,
);

export const API_PATH = pickConfig("API_PATH", parsedApiBaseUrl.path || "api");

export const API_BASE_URL = joinUrl(API_ORIGIN, API_PATH);

export const REALTIME_HUB_PATH = pickConfig("REALTIME_HUB_PATH", "hubs");

export const REALTIME_HUB_URL = joinUrl(API_ORIGIN, REALTIME_HUB_PATH);

export const PUBLIC_KEY = pickConfig(
  "PUBLIC_KEY",

  "MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEA2PkhjvWu+lDEv/ane+uVN44MAZBhWn2Xbr5zEu7h9LpXJXhrwKYhtvWR6YmjAR4AcXDwA3P74Hjc8/jsW92Q5B4ddXJrRbsU3lac1GhjCNma31FlW7Mpjr5eqPNTuImJr1WgDR9iRuCFYt4enRkvfywdnDa++QK6fdjS4/kssJxlEXBtlXKoFuSBGjbf0JA56qHo8yqXoYoqgl9Z7e9X8GZv6soB1JDH9dxMmaqEwsxaonDG+8NdR2RcYeJAnx2s/PBokpbCVPCQiSD5mmYWSZePF7L4mqvYS7ByrIj1cBH8qq6TafTcLkrr/TiZjpAADPwuynQDE120BpFaqIEqlQIDAQAB",
);

export const VIDEO_INDEXER_ACCOUNT_ID = pickConfig(
  "VIDEO_INDEXER_ACCOUNT_ID",

  "806a2fcb-7c5a-40bb-a184-4f9421cb8068",
);

export const VIDEO_INDEXER_BASE_URL = pickConfig(
  "VIDEO_INDEXER_BASE_URL",

  "https://api.videoindexer.ai/internals",
);

export const VIDEO_INDEXER_REGION = pickConfig(
  "VIDEO_INDEXER_REGION",

  "centralindia",
);

export const GTM_CONTAINER_ID = pickConfig(
  "GTM_CONTAINER_ID",

  "GTM-KV8F2WTF",
);

export const GA_MEASUREMENT_ID = pickConfig(
  "GA_MEASUREMENT_ID",

  "G-VY0LG9HEKF",
);

export const GTM_SCRIPT_URL = pickConfig(
  "GTM_SCRIPT_URL",

  "https://www.googletagmanager.com/gtm.js",
);

export const GA_SCRIPT_URL = pickConfig(
  "GA_SCRIPT_URL",

  "https://www.googletagmanager.com/gtag/js",
);

export const CDN_ORIGIN = pickConfig("CDN_ORIGIN", DEFAULT_CDN_ORIGIN);

export const CDN_FILES_PATH = pickConfig("CDN_FILES_PATH", "Files/Wlj");

export const CDN_ASSETS_PATH = pickConfig("CDN_ASSETS_PATH", "assets");

/** Root CDN folder for app files (e.g. favicon, sounds) */

export const CDN_BASE_URL = pickConfig("CDN_BASE_URL", "");

export const CDN_PUBLIC_PATH = pickConfig(
  "CDN_PUBLIC_PATH",

  joinUrl(CDN_ORIGIN, CDN_FILES_PATH),
);

/** Migrated /src/assets */

export const CDN_ASSETS_BASE_URL = pickConfig(
  "CDN_ASSETS_BASE_URL",

  joinUrl(CDN_PUBLIC_PATH, CDN_ASSETS_PATH),
);

export const SITE_ORIGIN = pickConfig("SITE_ORIGIN", "https://welinkjobs.com");

export const NOT_FOUND_URL = pickConfig(
  "NOT_FOUND_URL",
  "https://wlj.app/NotFound",
);

export const ADMIN_APP_ORIGIN = pickConfig("ADMIN_APP_ORIGIN", "https://admin-test.wlj.app");



const withQuery = (url, params) => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.set(key, value);
    }
  });

  const query = searchParams.toString();

  return query ? `${url}?${query}` : url;
};

export const buildGtmScriptUrl = (containerId) =>
  withQuery(GTM_SCRIPT_URL, { id: containerId });

export const buildGaScriptUrl = (measurementId) =>
  withQuery(GA_SCRIPT_URL, { id: measurementId });

export const buildVideoIndexerManifestUrl = ({
  videoId,

  accessToken,

  region = VIDEO_INDEXER_REGION,

  account = VIDEO_INDEXER_ACCOUNT_ID,
}) =>
  withQuery(
    `${joinUrl(
      VIDEO_INDEXER_BASE_URL,

      `${region}/Accounts/${account}/Videos/${videoId}/streaming-manifest/manifest.m3u8`,
    )}`,

    { accessToken },
  );

export const buildVideoIndexerCaptionsUrl = ({
  videoId,

  accessToken,

  format = "vtt",

  region = VIDEO_INDEXER_REGION,

  account = VIDEO_INDEXER_ACCOUNT_ID,
}) =>
  withQuery(
    `${joinUrl(
      VIDEO_INDEXER_BASE_URL,

      `${region}/Accounts/${account}/Videos/${videoId}/captions`,
    )}`,

    { format, accessToken },
  );
